import * as THREE from 'three';
import { ft } from '../core/units.js';
import { LINENS, OBJECTS } from '../data/objects.js';
import { CHAIR_NEST, CHAIR_SEAT, banquetChair, linenStack, merged, riserDeck, roundTable, signEasel } from './models.js';
import { propGeometry, propMaterials } from './props.js';

/**
 * Draws every loose object in the world, and everything currently in the air.
 *
 * Pooled by shape rather than by kind, because there are now dozens of kinds
 * and there will be more. Three primitives cover all of them — a box, a
 * cylinder, a sphere — scaled and coloured from the catalogue row, so adding an
 * object is adding data and never touching this file.
 *
 * Except the banquet furniture, which the whole game is about and which is
 * everywhere: a round, a chair, a linen, a riser and a signage board are drawn
 * as what they are (`models.js`), at the size their row gives, pooled by kind.
 * The row's colour is still the colour: the round's top, the chair's cushion,
 * the linen's cloth, the board.
 *
 * And everything else small enough to pick up is drawn as what it is too
 * (`props.js`): a keg, a suitcase, an orange, the fire extinguisher, sized to
 * its row. The three primitives remain for a kind nobody has modelled yet.
 *
 * A thing put down on a loose chair sits on its seat, forward of the back,
 * and a chair put down on a chair nests into it as a stack does.
 */
const SHAPES = ['box', 'cylinder', 'sphere'];

const METAL = 0x1000000;
/** Kinds drawn as a model: how to build one from its row, and each part's colour. */
const MODELS = {
  round: { build: () => roundTable(), parts: ['top', 'legs'], colours: (row) => [row.colour, 0x3a3a3c | METAL] },
  chair: { build: () => banquetChair(), parts: ['frame', 'cushion'], colours: (row) => [0xb8963e | METAL, row.colour] },
  linen: {
    build: (row) => linenStack(4, ft(row.radius * 2), ft(row.radius * 2) * 0.78),
    parts: ['linen'],
    colours: (row, colour) => [colour],
  },
  riser: { build: (row) => riserDeck(ft(row.radius * 2), ft(row.radius * 2), ft(row.height)), parts: ['top', 'frame'], colours: (row) => [0x3a3c40, row.colour] },
  signage: { build: (row) => signEasel(ft(row.radius * 2) * 0.7, ft(row.height)), parts: ['board', 'frame'], colours: (row) => [row.colour, 0xc9a35b | METAL] },
};

export function createEntityView({ entities, throwing }) {
  const group = new THREE.Group();
  group.name = 'loose-objects';

  const geometries = new Map();
  const materials = new Map();
  const disposables = [];

  function geometryFor(shape) {
    let found = geometries.get(shape);
    if (!found) {
      found =
        shape === 'sphere'
          ? new THREE.SphereGeometry(0.5, 10, 7)
          : shape === 'cylinder'
            ? new THREE.CylinderGeometry(0.5, 0.5, 1, 12)
            : new THREE.BoxGeometry(1, 1, 1);
      geometries.set(shape, found);
      disposables.push(found);
    }
    return found;
  }

  function materialFor(colour) {
    let found = materials.get(colour);
    if (!found) {
      const metal = colour >= METAL;
      found = new THREE.MeshStandardMaterial({ color: colour % METAL, roughness: metal ? 0.35 : 0.9, metalness: metal ? 0.7 : 0 });
      materials.set(colour, found);
      disposables.push(found);
    }
    return found;
  }

  /** One pool per shape; a mesh is re-pointed at a different material as needed. */
  const pools = new Map(SHAPES.map((shape) => [shape, []]));
  const used = new Map(SHAPES.map((shape) => [shape, 0]));

  function take(shape) {
    const pool = pools.get(shape);
    const index = used.get(shape);
    used.set(shape, index + 1);
    while (pool.length <= index) {
      const mesh = new THREE.Mesh(geometryFor(shape), materialFor(0xffffff));
      mesh.castShadow = true;
      mesh.visible = false;
      group.add(mesh);
      pool.push(mesh);
    }
    return pool[index];
  }

  /** One pool per modelled kind, sharing that kind's one geometry. */
  const modelGeometries = new Map();
  const modelPools = new Map();
  const modelUsed = new Map();
  /** A prop's geometry, once per kind; null for a kind with no model. */
  const props = new Map();
  function propFor(kind, definition) {
    if (!props.has(kind)) {
      const geometry = propGeometry(kind, ft((definition.radius ?? 0.4) * 2), ft(definition.height ?? 0.4), definition.colour);
      if (geometry) disposables.push(geometry);
      props.set(kind, geometry);
    }
    return props.get(kind);
  }
  function takeModel(kind, definition) {
    let geometry = modelGeometries.get(kind);
    if (!geometry) {
      geometry = MODELS[kind] ? merged(MODELS[kind].build(definition), MODELS[kind].parts) : propFor(kind, definition);
      modelGeometries.set(kind, geometry);
      if (MODELS[kind]) disposables.push(geometry);
    }
    if (!modelPools.has(kind)) {
      modelPools.set(kind, []);
      modelUsed.set(kind, 0);
    }
    const pool = modelPools.get(kind);
    const index = modelUsed.get(kind);
    modelUsed.set(kind, index + 1);
    while (pool.length <= index) {
      const mesh = new THREE.Mesh(geometry, MODELS[kind] ? MODELS[kind].parts.map(() => materialFor(0xffffff)) : propMaterials());
      mesh.castShadow = true;
      mesh.visible = false;
      group.add(mesh);
      pool.push(mesh);
    }
    return pool[index];
  }

  function draw(kind, variant, position, yaw = 0) {
    const definition = OBJECTS[kind];
    if (!definition) return;
    if (!MODELS[kind] && propFor(kind, definition)) {
      const mesh = takeModel(kind, definition);
      mesh.position.set(position.x, position.y ?? 0, position.z);
      mesh.rotation.y = yaw;
      mesh.visible = true;
      return;
    }
    const colourOf = () =>
      variant && definition.variantSet === 'linenColour'
        ? LINENS[variant]?.colour ?? definition.colour
        : definition.colour;
    if (MODELS[kind]) {
      const mesh = takeModel(kind, definition);
      mesh.position.set(position.x, position.y ?? 0, position.z);
      mesh.rotation.y = yaw;
      const colours = MODELS[kind].colours(definition, colourOf());
      colours.forEach((colour, i) => {
        mesh.material[i] = materialFor(colour);
      });
      mesh.visible = true;
      return;
    }
    const shape = SHAPES.includes(definition.shape) ? definition.shape : 'box';
    const mesh = take(shape);
    const width = ft((definition.radius ?? 0.4) * 2);
    const height = ft(definition.height ?? 0.4);
    mesh.scale.set(width, height, width);
    mesh.position.set(position.x, (position.y ?? 0) + height / 2, position.z);
    mesh.rotation.y = yaw;
    mesh.material = materialFor(colourOf());
    mesh.visible = true;
  }

  /**
   * Where a thing set down on a loose chair is drawn.
   *
   * The game lets anything come to rest where a chair is standing, and puts
   * it on the floor there, which drew a bottle standing between the chair's
   * legs. Drawn on the seat instead, and forward of the back by as much as it
   * needs; a chair set down on a chair nests into it as a stack does. Only
   * the drawing moves: to the game it is where it always was.
   */
  const CHAIR_TOP = ft(OBJECTS.chair.height);
  function seatUnder(entity, chairs) {
    for (const chair of chairs) {
      if (chair === entity) continue;
      const dx = entity.position.x - chair.position.x;
      const dz = entity.position.z - chair.position.z;
      if (Math.hypot(dx, dz) > 0.24) continue;
      const base = chair.position.y ?? 0;
      const y = entity.position.y ?? 0;
      const onTop = Math.abs(y - (base + CHAIR_TOP)) < 0.12;
      const under = Math.abs(y - base) < 0.05 && ft(OBJECTS[entity.kind]?.height ?? 1) < CHAIR_TOP;
      if (!onTop && !under && entity.kind !== 'chair') continue;
      // Of two chairs in one place, the later is the one on top.
      if (entity.kind === 'chair' && chairs.indexOf(entity) < chairs.indexOf(chair)) continue;
      const yaw = chair.yaw ?? 0;
      if (entity.kind === 'chair') {
        return { position: { x: chair.position.x - Math.sin(yaw) * 0.012, y: base + CHAIR_NEST, z: chair.position.z - Math.cos(yaw) * 0.012 }, yaw };
      }
      const half = ft(OBJECTS[entity.kind]?.radius ?? 0.3);
      const forward = Math.min(0.14, Math.max(0, half - 0.17));
      return {
        position: { x: entity.position.x + Math.sin(yaw) * forward, y: base + CHAIR_SEAT, z: entity.position.z + Math.cos(yaw) * forward },
        yaw: entity.yaw,
      };
    }
    return null;
  }

  return {
    object3D: group,

    sync() {
      for (const shape of SHAPES) used.set(shape, 0);
      for (const kind of modelUsed.keys()) modelUsed.set(kind, 0);
      const chairs = [];
      for (const entity of entities.all) if (entity.state === 'world' && entity.kind === 'chair') chairs.push(entity);
      for (const entity of entities.all) {
        if (entity.state !== 'world') continue;
        const seat = chairs.length ? seatUnder(entity, chairs) : null;
        if (seat) draw(entity.kind, entity.variant, seat.position, seat.yaw);
        else draw(entity.kind, entity.variant, entity.position, entity.yaw);
      }
      for (const shot of throwing.flying) {
        draw(shot.kind, shot.variant, shot.position);
      }
      for (const shape of SHAPES) {
        const pool = pools.get(shape);
        for (let i = used.get(shape); i < pool.length; i += 1) pool[i].visible = false;
      }
      for (const [kind, pool] of modelPools) {
        for (let i = modelUsed.get(kind); i < pool.length; i += 1) pool[i].visible = false;
      }
    },

    dispose() {
      for (const thing of disposables) thing.dispose?.();
      group.removeFromParent();
    },
  };
}
